import { useState, useCallback, useEffect } from 'react';
import roiService from '../services/roiService';
import { findBestROIForPin, isValidPolygon } from '../../../utils/roiUtils';

/**
 * Custom hook for managing ROI (Region of Interest) state and operations
 * @param {string} floorplanId - The ID of the current floorplan
 * @returns {Object} ROI state and operations
 */
export const useROI = (floorplanId) => {
    // ROI State
    const [rois, setROIs] = useState([]);
    const [currentPolygon, setCurrentPolygon] = useState([]);
    const [isDrawingMode, setIsDrawingMode] = useState(false);
    const [drawingMode, setDrawingMode] = useState(null); // 'rectangle', 'polygon', or null
    const [rectangleStart, setRectangleStart] = useState(null); // Starting point for rectangle
    const [isDeleteMode, setIsDeleteMode] = useState(false);
    const [isROIVisible, setIsROIVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showROIModal, setShowROIModal] = useState(false);

    // Room selection and search state
    const [selectedRoomId, setSelectedRoomId] = useState(null);
    const [roomSearchQuery, setRoomSearchQuery] = useState('');

    /**
     * Load ROIs for the current floorplan
     */
    const loadROIs = useCallback(async () => {
        if (!floorplanId) {
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const loadedROIs = await roiService.getROIs(floorplanId);
            setROIs(loadedROIs);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [floorplanId]);

    // Load existing ROIs when floorplan changes
    useEffect(() => {
        if (floorplanId) {
            loadROIs();
        }
    }, [floorplanId, loadROIs]);

    /**
     * Start drawing a new rectangle ROI
     */
    const startRectangleDrawing = useCallback(() => {
        setIsDrawingMode(true);
        setDrawingMode('rectangle');
        setIsDeleteMode(false);
        setCurrentPolygon([]);
        setRectangleStart(null);
        setError(null);
    }, []);

    /**
     * Start drawing a new polygon ROI
     */
    const startPolygonDrawing = useCallback(() => {
        setIsDrawingMode(true);
        setDrawingMode('polygon');
        setIsDeleteMode(false);
        setCurrentPolygon([]);
        setRectangleStart(null);
        setError(null);
    }, []);

    /**
     * Start delete mode for ROIs
     */
    const startROIDeleteMode = useCallback(() => {
        setIsDeleteMode(true);
        setIsDrawingMode(false); // Exit drawing mode when starting to delete
        setError(null);
    }, []);

    /**
     * Stop delete mode
     */
    const stopROIDeleteMode = useCallback(() => {
        setIsDeleteMode(false);
        setError(null);
    }, []);

    /**
     * Add a point to the current polygon being drawn
     * @param {Array} point - [x, y] normalized coordinates (0-1)
     */
    const addPolygonPoint = useCallback(
        (point) => {
            if (!isDrawingMode) return;

            setCurrentPolygon((prev) => [...prev, point]);
        },
        [isDrawingMode]
    );

    /**
     * Complete rectangle drawing by converting it to a 4-point polygon
     * @param {Array} endPoint - [x, y] normalized coordinates for the end corner
     */
    const completeRectangle = useCallback(
        (endPoint) => {
            if (!rectangleStart || !endPoint) {
                setError('Invalid rectangle coordinates');
                return;
            }

            // Create 4-point polygon from rectangle corners
            const [x1, y1] = rectangleStart;
            const [x2, y2] = endPoint;

            // Create rectangle as polygon: [topLeft, topRight, bottomRight, bottomLeft]
            const rectanglePolygon = [
                [Math.min(x1, x2), Math.min(y1, y2)], // Top-left
                [Math.max(x1, x2), Math.min(y1, y2)], // Top-right
                [Math.max(x1, x2), Math.max(y1, y2)], // Bottom-right
                [Math.min(x1, x2), Math.max(y1, y2)], // Bottom-left
            ];

            if (!isValidPolygon(rectanglePolygon)) {
                setError(
                    'Invalid rectangle. Please ensure all points are within the image bounds.'
                );
                return;
            }

            setCurrentPolygon(rectanglePolygon);
            setShowROIModal(true);
        },
        [rectangleStart]
    );

    /**
     * Complete the current ROI drawing and show naming modal
     */
    const completeROIDrawing = useCallback(() => {
        if (!isDrawingMode || currentPolygon.length < 3) {
            setError('ROI must have at least 3 points');
            return;
        }

        if (!isValidPolygon(currentPolygon)) {
            setError('Invalid polygon. Please ensure all points are within the image bounds.');
            return;
        }

        setShowROIModal(true);
    }, [isDrawingMode, currentPolygon]);

    /**
     * Cancel the current ROI drawing or delete mode
     */
    const cancelROIDrawing = useCallback(() => {
        setIsDrawingMode(false);
        setDrawingMode(null);
        setIsDeleteMode(false);
        setCurrentPolygon([]);
        setRectangleStart(null);
        setError(null);
    }, []);

    /**
     * Remove the last point from the current polygon
     */
    const undoLastPoint = useCallback(() => {
        if (!isDrawingMode || currentPolygon.length === 0) return;

        setCurrentPolygon((prev) => prev.slice(0, -1));
        setError(null); // Clear any errors when undoing
    }, [isDrawingMode, currentPolygon.length]);

    /**
     * Save a new ROI with the given name
     * @param {Object} roiData - Object containing name and polygon
     */
    const saveROI = useCallback(
        async (roiData) => {
            if (!floorplanId) {
                setError('No floorplan selected');
                return;
            }

            setIsLoading(true);
            setError(null);

            try {
                // Validate ROI data
                const validation = roiService.validateROIs([roiData]);
                if (!validation.isValid) {
                    throw new Error(validation.errors.join(', '));
                }

                // Save to backend
                const response = await roiService.saveROIs(floorplanId, [roiData]);

                // Reload ROIs from backend to get the complete data with ID and timestamp
                await loadROIs();

                // Reset drawing state - clear current shape but keep drawing mode active
                // This allows users to quickly add another room
                setCurrentPolygon([]);
                setRectangleStart(null);
                setShowROIModal(false);

                return response;
            } catch (err) {
                setError(err.message);
                // Revert local state on error
                loadROIs();
                throw err;
            } finally {
                setIsLoading(false);
            }
        },
        [floorplanId, loadROIs]
    );

    /**
     * Delete an ROI
     * @param {string} roiId - ID of the ROI to delete
     */
    const deleteROI = useCallback(
        async (roiId) => {
            if (!floorplanId) {
                setError('No floorplan selected');
                return;
            }

            setIsLoading(true);
            setError(null);

            try {
                // Update local state immediately (optimistic update)
                const updatedROIs = rois.filter((roi) => roi.id !== roiId);
                setROIs(updatedROIs);

                // Delete from backend
                await roiService.deleteROI(floorplanId, roiId);
            } catch (err) {
                setError(err.message);
                // Revert local state on error
                loadROIs();
                throw err;
            } finally {
                setIsLoading(false);
            }
        },
        [floorplanId, rois, loadROIs]
    );

    /**
     * Toggle ROI visibility
     */
    const toggleROIVisibility = useCallback(() => {
        setIsROIVisible((prev) => !prev);
    }, []);

    /**
     * Set ROI visibility directly
     * @param {boolean} visible - Whether ROIs should be visible
     */
    const setROIVisible = useCallback((visible) => {
        setIsROIVisible(visible);
    }, []);

    /**
     * Get the best ROI assignment for a pin
     * @param {Object} pin - Pin object with xPct, yPct properties
     * @returns {Object|null} Best matching ROI or null
     */
    const getROIForPin = useCallback(
        (pin) => {
            return findBestROIForPin(pin, rois);
        },
        [rois]
    );

    /**
     * Get ROI names for validation (prevent duplicates)
     */
    const getExistingROINames = useCallback(() => {
        return rois.map((roi) => roi.name);
    }, [rois]);

    /**
     * Select/highlight a room by ID
     * @param {string} roomId - ID of the room to select
     */
    const selectRoom = useCallback((roomId) => {
        setSelectedRoomId(roomId);
    }, []);

    /**
     * Clear room selection
     */
    const clearRoomSelection = useCallback(() => {
        setSelectedRoomId(null);
    }, []);

    /**
     * Update search query for filtering rooms
     * @param {string} query - Search query string
     */
    const updateRoomSearch = useCallback((query) => {
        setRoomSearchQuery(query);
    }, []);

    /**
     * Get filtered rooms based on search query
     */
    const filteredRooms = useCallback(() => {
        if (!roomSearchQuery.trim()) {
            return rois;
        }

        const lowerQuery = roomSearchQuery.toLowerCase();
        return rois.filter((roi) => roi.name.toLowerCase().includes(lowerQuery));
    }, [rois, roomSearchQuery]);

    /**
     * Rename an existing ROI
     * @param {string} roiId - ID of the ROI to rename
     * @param {string} newName - New name for the ROI
     */
    const renameROI = useCallback(
        async (roiId, newName) => {
            if (!floorplanId) {
                setError('No floorplan selected');
                return;
            }

            if (!newName || !newName.trim()) {
                setError('Room name cannot be empty');
                return;
            }

            setIsLoading(true);
            setError(null);

            try {
                // Update local state immediately (optimistic update)
                const updatedROIs = rois.map((roi) =>
                    roi.id === roiId ? { ...roi, name: newName.trim() } : roi
                );
                setROIs(updatedROIs);

                // Update on backend
                await roiService.updateROI(floorplanId, roiId, { name: newName.trim() });
            } catch (err) {
                setError(err.message);
                // Revert local state on error
                loadROIs();
                throw err;
            } finally {
                setIsLoading(false);
            }
        },
        [floorplanId, rois, loadROIs]
    );

    /**
     * Clear all error states
     */
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    /**
     * Close the ROI naming modal
     */
    const closeROIModal = useCallback(() => {
        setShowROIModal(false);
    }, []);

    /**
     * Clear all drawing state (used when exiting Edit Mode)
     */
    const clearDrawingState = useCallback(() => {
        setIsDrawingMode(false);
        setDrawingMode(null);
        setCurrentPolygon([]);
        setRectangleStart(null);
        setIsDeleteMode(false);
        setShowROIModal(false);
        setSelectedRoomId(null);
        setError(null);
    }, []);

    return {
        // State
        rois,
        currentPolygon,
        isDrawingMode,
        drawingMode,
        rectangleStart,
        isDeleteMode,
        isROIVisible,
        isLoading,
        error,
        showROIModal,
        selectedRoomId,
        roomSearchQuery,

        // Operations
        loadROIs,
        startRectangleDrawing,
        startPolygonDrawing,
        startROIDeleteMode,
        stopROIDeleteMode,
        addPolygonPoint,
        completeROIDrawing,
        completeRectangle,
        cancelROIDrawing,
        undoLastPoint,
        saveROI,
        deleteROI,
        renameROI,
        toggleROIVisibility,
        setROIVisible,
        closeROIModal,
        clearError,
        clearDrawingState,
        setRectangleStart,

        // Room selection and search
        selectRoom,
        clearRoomSelection,
        updateRoomSearch,
        filteredRooms,

        // Utility functions
        getROIForPin,
        getExistingROINames,
    };
};
